<template>
  <Transition name="slide-down">
    <div v-if="showUpdate" class="update-notification">
      <div class="update-content">
        <span class="material-icons update-icon">system_update</span>
        <span class="update-text">גרסה חדשה זמינה!</span>
      </div>
      <button class="update-btn" @click="performUpdate" :disabled="isUpdating">
        <span v-if="isUpdating" class="material-icons spinning">refresh</span>
        <span v-else class="material-icons">refresh</span>
        {{ isUpdating ? 'מעדכן...' : 'עדכן עכשיו' }}
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const showUpdate = ref(false);
const isUpdating = ref(false);
const serviceWorkerRegistration = ref(null);

// Session key to prevent showing notification immediately after update
const UPDATE_PERFORMED_KEY = 'pwa-update-just-performed';

// Check if we just performed an update (to prevent double notification)
const justPerformedUpdate = () => {
  const performed = sessionStorage.getItem(UPDATE_PERFORMED_KEY);
  if (performed) {
    // Clear the flag after checking
    sessionStorage.removeItem(UPDATE_PERFORMED_KEY);
    return true;
  }
  return false;
};

// Handle the service worker update event (same event used by existing system)
const handleUpdateEvent = (e) => {
  // Skip if we just performed an update
  if (justPerformedUpdate()) return;

  serviceWorkerRegistration.value = e.detail || null;
  showUpdate.value = true;
};

// Clear all caches for a clean update
const clearAllCaches = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('All caches cleared');
    } catch (error) {
      console.error('Error clearing caches:', error);
    }
  }
};

// Perform the update with hard refresh
const performUpdate = async () => {
  if (isUpdating.value) return;
  isUpdating.value = true;

  try {
    // Mark that we're performing an update (to prevent double notification)
    sessionStorage.setItem(UPDATE_PERFORMED_KEY, 'true');

    // Step 1: Clear all browser caches
    await clearAllCaches();

    // Step 2: If there's a waiting service worker, tell it to activate
    if (serviceWorkerRegistration.value?.waiting) {
      serviceWorkerRegistration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    // Step 3: Small delay to allow SW to activate
    await new Promise(resolve => setTimeout(resolve, 500));

    // Step 4: Hard reload - bypasses cache completely
    // Remove any existing cache-busting params and add new one
    const url = new URL(window.location.origin + window.location.pathname);
    url.searchParams.set('_v', Date.now().toString());

    // Perform hard navigation
    window.location.replace(url.toString());

  } catch (error) {
    console.error('Update failed:', error);
    // Fallback: force reload
    sessionStorage.setItem(UPDATE_PERFORMED_KEY, 'true');
    window.location.reload();
  }
};

onMounted(() => {
  // Check if we just performed an update - if so, skip showing notification
  if (justPerformedUpdate()) {
    return;
  }

  // Listen for update events (same event dispatched by registerServiceWorker.js)
  document.addEventListener('serviceWorkerUpdateEvent', handleUpdateEvent);

  // Also check if there's already a waiting service worker on mount
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      if (registration.waiting) {
        serviceWorkerRegistration.value = registration;
        showUpdate.value = true;
      }

      // Listen for future updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content available
              serviceWorkerRegistration.value = registration;
              showUpdate.value = true;
            }
          });
        }
      });
    });
  }
});

onUnmounted(() => {
  document.removeEventListener('serviceWorkerUpdateEvent', handleUpdateEvent);
});
</script>

<style scoped>
.update-notification {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100000;
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  color: white;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  direction: rtl;
}

.update-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.update-icon {
  font-size: 24px;
  color: white;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.update-text {
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.update-btn {
  background: white;
  color: #1976D2;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}

.update-btn:hover:not(:disabled) {
  background: #E3F2FD;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.update-btn:disabled {
  opacity: 0.8;
  cursor: wait;
}

.update-btn .material-icons {
  font-size: 18px;
  color: #1976D2;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Slide down animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .update-notification {
    flex-direction: column;
    padding: 15px;
    gap: 12px;
  }

  .update-content {
    width: 100%;
    justify-content: center;
  }

  .update-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
